import classNames from 'classnames';
import { FunctionComponent, useEffect, useRef } from 'react';
import { FieldError, useForm } from 'react-hook-form';

import { MessageBundle } from '@amzn/arb-tools';
import { RelationToAmazon } from '@amzn/coral_com-amazon-asxcustomerprofileservice';
import {
  Icon,
  PRIMARY_BUTTON_SIZE,
  PrimaryButton,
  TextInput,
  DropdownInputMultiSelection,
  useNavbarPositioning,
  useMarketplaceRedirect,
  redirectToCountry,
  ASX_COUNTRY_CODE,
  getCountryOptions,
  ICON_NAME,
} from '@amzn/sss-website-theme';

import { useAcpsCreditsClient } from 'src/clients/acps';
import { isUkLaunchEnabled } from 'src/clients/weblab/weblab-client';
import {
  OrganizationInformationFormValues,
  shortInputMaxLength,
  INTAKE_FORM_KEYS,
  emailRegex,
  validateName,
  validateFreeFormText,
  relationToAmazonTranslationMap,
  relationToAmazonDescriptionTranslationMap,
  SUPPORTED_COUNTRIES,
} from 'src/components/pages/intake-form/FormEnums';
import { FormStrings, MarketplaceRedirectStrings } from 'src/constants';
import { addRequiredLabelSymbol, t } from 'src/helpers';
import { useGetUserRights } from 'src/hooks';

import styles from './OrganizationInformationForm.module.scss';

export interface OrganizationInformationFormProp {
  intakeFormBundle: MessageBundle;
  handleFormSubmit: (data: OrganizationInformationFormValues) => void;
  ctaLabel: string;
  defaultValues?: Partial<OrganizationInformationFormValues>;
  isRebrandingActive?: boolean;
}

export const OrganizationInformationForm: FunctionComponent<OrganizationInformationFormProp> = ({
  intakeFormBundle,
  handleFormSubmit,
  ctaLabel,
  defaultValues = {},
  isRebrandingActive,
}) => {
  const { getPrimaryContactInformation } = useAcpsCreditsClient();
  const { isAuthenticated } = useGetUserRights();
  const { mainNavbarHeight } = useNavbarPositioning();
  const { checkAndPromptRedirect } = useMarketplaceRedirect(isRebrandingActive);
  const errorsPresentRef = useRef<HTMLDivElement>(null);

  const informationFormId = 'informationform';
  const { register, handleSubmit, getValues, setValue, clearErrors, control, formState, watch } =
    useForm<OrganizationInformationFormValues>({
      reValidateMode: 'onSubmit',
      defaultValues,
    });
  const { errors } = formState;
  const formHasErrors = Object.keys(errors).length > 0;

  const isMandatoryCopy = t(intakeFormBundle, FormStrings.IS_MANDATORY);
  const tooLongInputCopy = `${t(intakeFormBundle, FormStrings.INPUT_TOO_LONG_LABEL)} ${shortInputMaxLength}`;

  const REDIRECT_COUNTRIES = [
    { code: ASX_COUNTRY_CODE.US, label: '🇺🇸 United States' },
    { code: ASX_COUNTRY_CODE.GB, label: '🇬🇧 United Kingdom' },
  ];

  const promptRedirectIfMismatch = (countryCode: string): boolean => {
    if (!isUkLaunchEnabled) return false;
    return checkAndPromptRedirect(countryCode as ASX_COUNTRY_CODE, {
      countries: REDIRECT_COUNTRIES,
      title: t(intakeFormBundle, MarketplaceRedirectStrings.TITLE),
      body: t(intakeFormBundle, MarketplaceRedirectStrings.BODY),
      confirmText: t(intakeFormBundle, MarketplaceRedirectStrings.CONFIRM_TEXT),
      dropdownLabel: t(intakeFormBundle, MarketplaceRedirectStrings.DROPDOWN_LABEL),
      dropdownSelectOptionCopy: t(intakeFormBundle, FormStrings.SELECT_OPTION),
      cancelButtonLabel: t(intakeFormBundle, MarketplaceRedirectStrings.CANCEL_BUTTON),
      confirmButtonLabel: t(intakeFormBundle, MarketplaceRedirectStrings.CONFIRM_BUTTON),
      onRedirect: (country) => redirectToCountry(country),
    });
  };

  // Name & Address fields are disabled when user is authenticated (contact info comes from API)
  const isFieldDisabled = isAuthenticated;

  useEffect(() => {
    if (formHasErrors && errorsPresentRef.current) {
      // We want to offset our scroll by our navbar height to avoid our errors present element being
      // hidden behind the navbar
      const navbarOffset = mainNavbarHeight;
      const topOffset = errorsPresentRef.current.getBoundingClientRect().top + window.scrollY - navbarOffset;
      window.scrollTo({ behavior: 'smooth', top: topOffset });
    }
  }, [formHasErrors, mainNavbarHeight]);

  useEffect(() => {
    // Clear errors when the component mounts
    clearErrors();
  }, [clearErrors]);

  useEffect(() => {
    const fetchPrimaryContact = async () => {
      try {
        const response = await getPrimaryContactInformation();

        // Set the values in react-hook-form so they're included in form submission
        setValue(INTAKE_FORM_KEYS.NAME, response.fullName ?? '');
        setValue(INTAKE_FORM_KEYS.EMAIL, response.emailAddress ?? '');
      } catch (error) {
        console.error('Failed to fetch primary contact information:', error);

        // Set empty values in react-hook-form
        setValue(INTAKE_FORM_KEYS.NAME, '');
        setValue(INTAKE_FORM_KEYS.EMAIL, '');
      }
    };

    if (isAuthenticated) {
      fetchPrimaryContact();
    }
  }, [getPrimaryContactInformation, setValue, isAuthenticated]);

  return (
    <div>
      {formHasErrors && (
        <div className={styles.formErrorsPresentWrapper} ref={errorsPresentRef}>
          <Icon name="error" className={styles.formErrorsPresentIcon} />
          <p className={styles.formErrorsPresent}>{t(intakeFormBundle, FormStrings.ERRORS_PRESENT_DESCRIPTION)}</p>
        </div>
      )}
      <div>
        <p className={styles.formRequiredFields}>{t(intakeFormBundle, FormStrings.REQUIRED_FIELDS_LABEL)}</p>
      </div>
      <div>
        <h4 className={styles.formSectionHeader}>{t(intakeFormBundle, FormStrings.PERSONAL_INFORMATION_HEADER)}</h4>
      </div>
      {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
      <form
        id={informationFormId}
        onSubmit={handleSubmit((data) => {
          if (promptRedirectIfMismatch(data[INTAKE_FORM_KEYS.COUNTRY_CODE])) return;
          handleFormSubmit(data);
        })}
        noValidate
      >
        {isUkLaunchEnabled && (
          <div className={styles.formInputWrapper}>
            <DropdownInputMultiSelection<OrganizationInformationFormValues>
              isRebrandingActive={isRebrandingActive}
              hideCheckbox
              singleSelect
              selectOptionCopy={t(intakeFormBundle, FormStrings.SELECT_OPTION)}
              label={addRequiredLabelSymbol(t(intakeFormBundle, FormStrings.COUNTRY_LABEL))}
              control={control}
              options={getCountryOptions()}
              errors={errors[INTAKE_FORM_KEYS.COUNTRY_CODE] as FieldError}
              {...register(INTAKE_FORM_KEYS.COUNTRY_CODE, {
                required: `${t(intakeFormBundle, FormStrings.COUNTRY_LABEL)}${isMandatoryCopy}`,
              })}
              onChange={(value, isChecked) => {
                if (isChecked && value) {
                  setValue(INTAKE_FORM_KEYS.COUNTRY_CODE, value);
                  promptRedirectIfMismatch(value);
                }
                clearErrors(INTAKE_FORM_KEYS.COUNTRY_CODE);
              }}
            />
          </div>
        )}

        <div className={styles.formInputWrapper}>
          <TextInput
            isRebrandingActive={isRebrandingActive}
            label={addRequiredLabelSymbol(t(intakeFormBundle, FormStrings.NAME_PLACEHOLDER_LABEL))}
            inputType="text"
            id={INTAKE_FORM_KEYS.NAME}
            value={watch(INTAKE_FORM_KEYS.NAME) ?? ''}
            {...register(INTAKE_FORM_KEYS.NAME, {
              required: `${t(intakeFormBundle, FormStrings.NAME_PLACEHOLDER_LABEL)}${isMandatoryCopy}`,
              maxLength: {
                value: shortInputMaxLength,
                message: tooLongInputCopy,
              },
              validate: (value) => validateName(value, t(intakeFormBundle, FormStrings.INVALID_INPUT_LABEL)),
            })}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              const fieldValue = event.target.value;
              setValue(INTAKE_FORM_KEYS.NAME, fieldValue);
              clearErrors(INTAKE_FORM_KEYS.NAME);
            }}
            errors={errors[INTAKE_FORM_KEYS.NAME]}
            disabled={isFieldDisabled}
          />
        </div>

        <div className={styles.formInputWrapper}>
          <TextInput
            isRebrandingActive={isRebrandingActive}
            label={addRequiredLabelSymbol(t(intakeFormBundle, FormStrings.EMAIL_PLACEHOLDER_LABEL))}
            inputType="text"
            id={INTAKE_FORM_KEYS.EMAIL}
            value={watch(INTAKE_FORM_KEYS.EMAIL) ?? ''}
            {...register(INTAKE_FORM_KEYS.EMAIL, {
              required: `${t(intakeFormBundle, FormStrings.EMAIL_PLACEHOLDER_LABEL)}${isMandatoryCopy}`,
              maxLength: {
                value: shortInputMaxLength,
                message: tooLongInputCopy,
              },
              pattern: {
                value: emailRegex,
                message: t(intakeFormBundle, FormStrings.INVALID_EMAIL_LABEL),
              },
            })}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              const fieldValue = event.target.value;
              setValue(INTAKE_FORM_KEYS.EMAIL, fieldValue);
              clearErrors(INTAKE_FORM_KEYS.EMAIL);
            }}
            errors={errors[INTAKE_FORM_KEYS.EMAIL]}
            disabled={isFieldDisabled}
          />
        </div>

        <div className={styles.formInputWrapper}>
          <TextInput
            isRebrandingActive={isRebrandingActive}
            label={addRequiredLabelSymbol(t(intakeFormBundle, FormStrings.COMPANY_LABEL))}
            inputType="text"
            id={INTAKE_FORM_KEYS.ORGANIZATION_NAME}
            value={getValues(INTAKE_FORM_KEYS.ORGANIZATION_NAME) ?? ''}
            {...register(INTAKE_FORM_KEYS.ORGANIZATION_NAME, {
              required: `${t(intakeFormBundle, FormStrings.COMPANY_LABEL)}${isMandatoryCopy}`,
              maxLength: {
                value: shortInputMaxLength,
                message: tooLongInputCopy,
              },
              validate: (value) => validateFreeFormText(value, t(intakeFormBundle, FormStrings.INVALID_INPUT_LABEL)),
            })}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              const fieldValue = event.target.value;
              setValue(INTAKE_FORM_KEYS.ORGANIZATION_NAME, fieldValue);
              clearErrors(INTAKE_FORM_KEYS.ORGANIZATION_NAME);
            }}
            errors={errors[INTAKE_FORM_KEYS.ORGANIZATION_NAME]}
          />
        </div>

        {!isUkLaunchEnabled && (
          <div className={styles.formInputWrapper}>
            <DropdownInputMultiSelection<OrganizationInformationFormValues>
              isRebrandingActive={isRebrandingActive}
              hideCheckbox
              singleSelect
              selectOptionCopy={t(intakeFormBundle, FormStrings.SELECT_OPTION)}
              label={addRequiredLabelSymbol(t(intakeFormBundle, FormStrings.COUNTRY_LABEL))}
              control={control}
              options={SUPPORTED_COUNTRIES}
              errors={errors[INTAKE_FORM_KEYS.COUNTRY_CODE] as FieldError}
              {...register(INTAKE_FORM_KEYS.COUNTRY_CODE, {
                required: `${t(intakeFormBundle, FormStrings.COUNTRY_LABEL)}${isMandatoryCopy}`,
              })}
              onChange={(value, isChecked) => {
                if (isChecked && value) {
                  setValue(INTAKE_FORM_KEYS.COUNTRY_CODE, value);
                }
                clearErrors(INTAKE_FORM_KEYS.COUNTRY_CODE);
              }}
            />
          </div>
        )}

        <div className={styles.formInputWrapper}>
          <DropdownInputMultiSelection
            isRebrandingActive={isRebrandingActive}
            selectOptionCopy={t(intakeFormBundle, FormStrings.SELECT_OPTION)}
            label={addRequiredLabelSymbol(t(intakeFormBundle, FormStrings.AMAZON_RELATION_LABEL))}
            control={control}
            options={Object.values(RelationToAmazon).map((relation) => ({
              value: relation,
              label: t(intakeFormBundle, relationToAmazonTranslationMap[relation], { relation }),

              description:
                relation === RelationToAmazon.OTHER
                  ? ''
                  : t(intakeFormBundle, relationToAmazonDescriptionTranslationMap[relation], { relation }),
            }))}
            errors={errors[INTAKE_FORM_KEYS.ORGANIZATION_RELATIONSHIP] as FieldError}
            {...register(INTAKE_FORM_KEYS.ORGANIZATION_RELATIONSHIP, {
              required: `${t(intakeFormBundle, FormStrings.AMAZON_RELATION_LABEL)}${isMandatoryCopy}`,
            })}
            onChange={(value, isChecked) => {
              const currentSelection: RelationToAmazon[] = getValues(INTAKE_FORM_KEYS.ORGANIZATION_RELATIONSHIP) ?? [];

              if (isChecked && value && !currentSelection.includes(value)) {
                setValue(INTAKE_FORM_KEYS.ORGANIZATION_RELATIONSHIP, [...currentSelection, value]);
              } else if (!isChecked && value && currentSelection.includes(value)) {
                setValue(
                  INTAKE_FORM_KEYS.ORGANIZATION_RELATIONSHIP,
                  currentSelection.filter((type) => type !== value)
                );
              }
              clearErrors(INTAKE_FORM_KEYS.ORGANIZATION_RELATIONSHIP);
            }}
          />
        </div>

        <div className={styles.submitButtonWrapper} data-testid="submitButtonWrapper">
          <PrimaryButton
            label={ctaLabel}
            isRebrandingActive={isRebrandingActive}
            icon={ICON_NAME.CHEVRON_RIGHT}
            type="submit"
            data-testid="submit-button"
            buttonSize={PRIMARY_BUTTON_SIZE.Small}
            form={informationFormId}
            className={classNames(!isRebrandingActive && styles.submitButton)}
          />
        </div>
      </form>
    </div>
  );
};
